// WhatsApp message content

// Demo messages - sequence of user messages for the auto demo
export const demoMessages = [
  "What's on your menu today?",
  "How long is the wait time right now?",
  "Do you have any vegetarian options?",
  "What are your most popular items?",
  "Where are you parked today?"
];

// Predefined tool responses for each demo message
export const demoResponses: Record<string, {tool: string, input: any, output: any}> = {
  "What's on your menu today?": {
    tool: 'menu_info',
    input: { 
      category: "all", 
      special_request: "today's menu"
    },
    output: { 
      daily_specials: [
        { name: "Loaded Street Tacos", price: "$12.99", description: "Three corn tortillas with slow-cooked pork, fresh cilantro, onions and lime" },
        { name: "Spicy Chicken Quesadilla", price: "$10.99", description: "Grilled chicken with jalapeños, melted cheese, and our secret spice blend" }
      ],
      regular_menu: {
        mains: [
          { name: "Classic Beef Burger", price: "$9.99", description: "1/3 lb smashed patty with lettuce, tomato, pickle and special sauce" },
          { name: "Fish Tacos", price: "$11.99", description: "Beer-battered cod with slaw, pico de gallo and lime crema" },
          { name: "Veggie Rice Bowl", price: "$10.99", description: "Brown rice, avocado, roasted vegetables and tahini dressing" }
        ],
        sides: [
          { name: "Loaded Fries", price: "$5.99", description: "Crispy fries topped with cheese, bacon and green onions" },
          { name: "Street Corn", price: "$4.99", description: "Grilled corn with cotija cheese, chili powder and lime" },
          { name: "Side Salad", price: "$3.99", description: "Mixed greens with cherry tomatoes and house vinaigrette" }
        ],
        drinks: [
          { name: "Fresh Lemonade", price: "$3.99", description: "Freshly squeezed lemons with a hint of mint" },
          { name: "Craft Sodas", price: "$2.99", description: "Various locally made craft sodas" },
          { name: "Iced Tea", price: "$2.49", description: "Unsweetened or sweet tea with optional fruit infusions" }
        ]
      },
      dietary_info: {
        vegetarian: 3,
        vegan: 1,
        gluten_free: 2
      }
    }
  },
  "How long is the wait time right now?": {
    tool: 'order_status',
    input: { 
      request_type: "wait_time", 
      current_time: "12:45pm"
    },
    output: { 
      current_wait: {
        ordering: "15 minutes",
        food_prep: "10-12 minutes",
        total_estimate: "25-30 minutes"
      },
      busy_level: "high",
      peak_hours: ["11:30am-1:30pm", "5:30pm-7:00pm"],
      quick_options: [
        { name: "Express Lunch Box", prep_time: "5 minutes", price: "$12.99" },
        { name: "Grab & Go Wraps", prep_time: "pre-made", price: "$8.99" }
      ],
      pre_order: {
        available: true,
        methods: ["Mobile app", "Website", "Phone call"],
        minimum_time: "30 minutes in advance"
      }
    }
  },
  "Do you have any vegetarian options?": {
    tool: 'menu_search',
    input: { 
      dietary: "vegetarian", 
      meal_type: "all",
      allergens: "none"
    },
    output: { 
      vegetarian_items: [
        {
          name: "Veggie Rice Bowl",
          price: "$10.99",
          description: "Brown rice, avocado, roasted seasonal vegetables and tahini dressing",
          popular: true,
          can_modify: true
        },
        {
          name: "Grilled Cheese Deluxe",
          price: "$8.99",
          description: "Three-cheese blend with caramelized onions on artisan sourdough",
          popular: false,
          can_modify: true
        },
        {
          name: "Loaded Veggie Nachos",
          price: "$9.99",
          description: "Tortilla chips with melted cheese, black beans, jalapeños, sour cream and guacamole",
          popular: true,
          can_modify: true
        }
      ],
      vegan_options: [
        {
          name: "Veggie Rice Bowl (no cheese)",
          price: "$10.99",
          modification: "Request no cheese and substitute tahini dressing"
        }
      ],
      sides: [
        { name: "Side Salad", price: "$3.99" },
        { name: "Street Corn (no cheese)", price: "$4.99" },
        { name: "Sweet Potato Fries", price: "$4.99" }
      ],
      allergen_info: "Our kitchen handles common allergens. Please inform us of any allergies."
    }
  },
  "What are your most popular items?": {
    tool: 'menu_highlights',
    input: { 
      filter: "popular", 
      limit: 5,
      include_reviews: true
    },
    output: { 
      top_items: [
        { 
          name: "Loaded Street Tacos",
          price: "$12.99",
          description: "Three corn tortillas with slow-cooked pork, fresh cilantro, onions and lime",
          rating: 4.8,
          order_count: "Daily Top Seller",
          reviews: ["Best tacos in town!", "The pork is so tender and flavorful"]
        },
        { 
          name: "Classic Beef Burger",
          price: "$9.99",
          description: "1/3 lb smashed patty with lettuce, tomato, pickle and special sauce",
          rating: 4.7,
          order_count: "2nd Most Popular",
          reviews: ["Perfect smash burger!", "That special sauce is amazing"]
        },
        { 
          name: "Loaded Veggie Nachos",
          price: "$9.99",
          description: "Tortilla chips with melted cheese, black beans, jalapeños, sour cream and guacamole",
          rating: 4.6,
          order_count: "Top Vegetarian Pick",
          reviews: ["Huge portion!", "Great for sharing"]
        },
        { 
          name: "Fish Tacos",
          price: "$11.99",
          description: "Beer-battered cod with slaw, pico de gallo and lime crema",
          rating: 4.5,
          order_count: "Crowd Favorite",
          reviews: ["So fresh!", "Perfect amount of crunch"]
        },
        { 
          name: "Fresh Lemonade",
          price: "$3.99",
          description: "Freshly squeezed lemons with a hint of mint",
          rating: 4.9,
          order_count: "Most Popular Drink",
          reviews: ["So refreshing!", "Not too sweet, just perfect"]
        }
      ],
      chef_recommendation: "Try our new Spicy Chicken Quesadilla this week!",
      customer_favorites: {
        lunch: "Loaded Street Tacos",
        dinner: "Fish Tacos",
        side: "Loaded Fries",
        dessert: "Churro Bites with Chocolate Dip"
      }
    }
  },
  "Where are you parked today?": {
    tool: 'location_info',
    input: { 
      day: "today", 
      location_type: "all",
      time: "current"
    },
    output: { 
      current_location: {
        address: "123 Main Street Food Truck Park",
        landmark: "Downtown near the public library",
        hours: "11:00 AM - 8:00 PM",
        coordinates: {
          latitude: "34.0522",
          longitude: "-118.2437"
        }
      },
      parking_info: "Street parking available. Free lot behind the library.",
      seating: "Limited picnic tables available in the food truck park area",
      upcoming_locations: [
        { day: "Tomorrow", location: "Tech Park on 5th Street", hours: "11:00 AM - 2:00 PM" },
        { day: "Friday", location: "Farmers Market on West Blvd", hours: "4:00 PM - 9:00 PM" },
        { day: "Saturday", location: "Beachside Boardwalk", hours: "12:00 PM - 7:00 PM" }
      ],
      map_link: "https://maps.app.goo.gl/example",
      directions: "From downtown, head east on Main Street. We're in the food truck park on the right, just past the library."
    }
  }
};

// Initial responses for each tool type
export const initialResponses: Record<string, string> = {
  menu_info: "Let me check today's menu for you...",
  order_status: "Let me check the current wait time...",
  menu_search: "Let me find our vegetarian options for you...",
  menu_highlights: "I'll show you our most popular items...",
  location_info: "Let me check where we're parked today..."
};

// Response templates for each tool type
export const responseTemplates: Record<string, string> = {
  menu_info: `Today's menu 🌮🍔\n\n*Specials:*\n{daily_specials}\n\n*Regular Menu:*\n{regular_menu}\n\n*Dietary:* Veg: {vegetarian} | Vegan: {vegan} | GF: {gluten_free}\n\nWant to place an order?`,
  
  order_status: `Current wait time ⏱️\n\n*Wait:* Line: {ordering} | Prep: {food_prep}\n*Total:* {total_estimate}\n*Busy Level:* {busy_level}\n\n*Quick Options:*\n{quick_options}\n\nPro Tip: Pre-order {minimum_time} via {methods}.`,
  
  menu_search: `Vegetarian options 🥗\n\n*Mains:*\n{vegetarian_items}\n\n*Sides:*\n{sides}\n\n*Vegan options:*\n{vegan_options}\n\n*Note:* {allergen_info}`,
  
  menu_highlights: `Fan favorites ⭐\n\n*Most Popular:*\n{top_items}\n\n*Chef's Recommendation:*\n{chef_recommendation}\n\n*Top picks:* Lunch: {lunch} | Dinner: {dinner}`,
  
  location_info: `Today's location 📍🚚\n\n*Current:* {address}\nNear: {landmark}\nHours: {hours}\n\n*Parking:* {parking_info}\n*Seating:* {seating}\n\n*Coming up:*\n{upcoming_locations}`
};

// Mock response templates for user interactions
export const mockResponseTemplates: Record<string, string> = {
  menu_info: `Weekend menu 🌮🍔\n\n*Specials:*\n{daily_specials}\n\n*Regular:*\n{regular_menu}\n\n*Dietary:* Veg: {vegetarian} | Vegan: {vegan} | GF: {gluten_free}`,
  
  order_status: `Order #12345 status ⏱️\n\n*Status:* {status}\n*Ready in:* {ready_time}\n*Items:* {items}\n\n*New orders wait:* {total_estimate}`,
  
  menu_search: `Gluten-free options 🥗\n\n*Mains:*\n{gluten_free_items}\n\n*Sides:*\n{sides}\n\n*Note:* {allergen_info}`,
  
  menu_highlights: `Breakfast favorites ⭐🍳\n\n*Top picks:*\n{top_items}\n\n*Chef recommends:* {chef_recommendation}`,
  
  location_info: `Weekly schedule 📍🚚\n\n*Now:* {address}\nHours: {hours}\n\n*Upcoming:*\n{upcoming_locations}`
};

// Sample mock data for realistic user interactions
// Helper function for formatting response templates
export const formatResponseFromTemplate = (template: string, data: any, templateType: string): string => {
  let formattedResponse = template;
  
  switch(templateType) {
    case 'menu_info': {
      const daily_specials = data.daily_specials?.map((item: any, idx: number) => 
        `${idx + 1}. *${item.name}* - ${item.price}\n   ${item.description.substring(0, 50)}${item.description.length > 50 ? '...' : ''}`
      ).join('\n\n') || 'No specials today';
      
      const mains = data.regular_menu?.mains?.map((item: any) => 
        `• *${item.name}* (${item.price})`
      ).join('\n') || '';
      
      const sides = data.regular_menu?.sides?.map((item: any) => 
        `• *${item.name}* (${item.price})`
      ).join('\n') || '';
      
      const drinks = data.regular_menu?.drinks?.map((item: any) => 
        `• *${item.name}* (${item.price})`
      ).join('\n') || '';
      
      const regular_menu = `*Mains:*\n${mains}\n\n*Sides:*\n${sides}\n\n*Drinks:*\n${drinks}`;
      const vegetarian = data.dietary_info?.vegetarian || 0;
      const vegan = data.dietary_info?.vegan || 0;
      const gluten_free = data.dietary_info?.gluten_free || 0;
      
      formattedResponse = template
        .replace('{daily_specials}', daily_specials)
        .replace('{regular_menu}', regular_menu)
        .replace('{vegetarian}', vegetarian.toString())
        .replace('{vegan}', vegan.toString())
        .replace('{gluten_free}', gluten_free.toString());
      break;
    }
    
    case 'order_status': {
      const ordering = data.current_wait?.ordering || 'N/A';
      const food_prep = data.current_wait?.food_prep || 'N/A';
      const total_estimate = data.current_wait?.total_estimate || 'N/A';
      const busy_level = data.busy_level || 'moderate';
      const quick_options = data.quick_options?.map((item: any) => 
        `• *${item.name}* - ${item.price} (${item.prep_time})`
      ).join('\n') || 'None available';
      const minimum_time = data.pre_order?.minimum_time || 'N/A';
      const methods = data.pre_order?.methods?.join('/') || 'N/A';
      
      const status = data.status || 'In preparation';
      const ready_time = data.ready_time || '15 minutes';
      const items = data.items?.join(', ') || 'Your ordered items';
      
      formattedResponse = template
        .replace('{ordering}', ordering)
        .replace('{food_prep}', food_prep)
        .replace('{total_estimate}', total_estimate)
        .replace('{busy_level}', busy_level)
        .replace('{quick_options}', quick_options)
        .replace('{minimum_time}', minimum_time)
        .replace('{methods}', methods)
        .replace('{status}', status)
        .replace('{ready_time}', ready_time)
        .replace('{items}', items);
      break;
    }
    
    case 'menu_search': {
      const vegetarian_items = data.vegetarian_items?.map((item: any, idx: number) => 
        `${idx + 1}. *${item.name}* - ${item.price}${item.popular ? ' ⭐' : ''}`
      ).join('\n') || 'None available';
      
      const sides = data.sides?.map((item: any) => 
        `• *${item.name}* - ${item.price}`
      ).join('\n') || 'None available';
      
      const vegan_options = data.vegan_options?.map((item: any) => 
        `• *${item.name}* - ${item.price}`
      ).join('\n') || 'None available';
      
      const allergen_info = data.allergen_info || 'Please inform us of any allergies';
      
      const gluten_free_items = data.gluten_free_items?.map((item: any, idx: number) => 
        `${idx + 1}. *${item.name}* - ${item.price}`
      ).join('\n') || 'None available';
      
      formattedResponse = template
        .replace('{vegetarian_items}', vegetarian_items)
        .replace('{sides}', sides)
        .replace('{vegan_options}', vegan_options)
        .replace('{allergen_info}', allergen_info)
        .replace('{gluten_free_items}', gluten_free_items);
      break;
    }
    
    case 'menu_highlights': {
      const top_items = data.top_items?.map((item: any, idx: number) => 
        `${idx + 1}. *${item.name}* - ${item.price} ⭐${item.rating}\n   "${item.reviews?.[0] || 'No reviews yet'}"`
      ).join('\n\n').substring(0, 300) || 'No data available';
      
      const chef_recommendation = data.chef_recommendation || 'Ask our staff';
      const lunch = data.customer_favorites?.lunch || 'varies daily';
      const dinner = data.customer_favorites?.dinner || 'varies daily';
      const side = data.customer_favorites?.side || 'varies daily';
      const dessert = data.customer_favorites?.dessert || 'varies daily';
      
      formattedResponse = template
        .replace('{top_items}', top_items)
        .replace('{chef_recommendation}', chef_recommendation)
        .replace('{lunch}', lunch)
        .replace('{dinner}', dinner)
        .replace('{side}', side)
        .replace('{dessert}', dessert);
      break;
    }
    
    case 'location_info': {
      const address = data.current_location?.address || 'Location unavailable';
      const landmark = data.current_location?.landmark || '';
      const hours = data.current_location?.hours || 'Call for hours';
      const parking_info = data.parking_info || 'Street parking';
      const seating = data.seating || 'No seating info';
      
      const upcoming_locations = data.upcoming_locations?.slice(0, 3).map((loc: any) => 
        `• *${loc.day}:* ${loc.location} (${loc.hours})`
      ).join('\n') || 'No upcoming info';
      
      const directions = data.directions || 'Use map link';
      const map_link = data.map_link || 'No map available';
      
      formattedResponse = template
        .replace('{address}', address)
        .replace('{landmark}', landmark)
        .replace('{hours}', hours)
        .replace('{parking_info}', parking_info)
        .replace('{seating}', seating)
        .replace('{upcoming_locations}', upcoming_locations)
        .replace('{directions}', directions)
        .replace('{map_link}', map_link);
      break;
    }
  }
  
  return formattedResponse;
};

export const mockData: Record<string, any> = {
  menu_info: {
    input: { 
      category: "weekend", 
      special_request: "brunch menu"
    },
    output: { 
      daily_specials: [
        { name: "Weekend Breakfast Burrito", price: "$11.99", description: "Scrambled eggs, chorizo, potato, cheese and salsa wrapped in a large flour tortilla" },
        { name: "Chicken & Waffles", price: "$13.99", description: "Crispy fried chicken on a Belgian waffle with maple hot honey" }
      ],
      regular_menu: {
        mains: [
          { name: "Breakfast Sandwich", price: "$8.99", description: "Egg, cheese and choice of bacon or sausage on a brioche bun" },
          { name: "Avocado Toast", price: "$9.99", description: "Multigrain toast with smashed avocado, cherry tomatoes and microgreens" }
        ],
        sides: [
          { name: "Hash Browns", price: "$3.99", description: "Crispy shredded potatoes" },
          { name: "Fruit Cup", price: "$4.99", description: "Seasonal fresh fruit" }
        ],
        drinks: [
          { name: "Cold Brew Coffee", price: "$4.99", description: "House-steeped for 24 hours" },
          { name: "Fresh Orange Juice", price: "$3.99", description: "Freshly squeezed daily" }
        ]
      },
      dietary_info: {
        vegetarian: 4,
        vegan: 2,
        gluten_free: 3
      }
    }
  },
  order_status: {
    input: { 
      order_id: "12345", 
      phone: "555-123-4567"
    },
    output: { 
      status: "In preparation",
      ready_time: "10-15 minutes",
      items: ["Classic Burger", "Loaded Fries", "Lemonade"],
      current_wait: {
        ordering: "20 minutes",
        food_prep: "15 minutes",
        total_estimate: "35 minutes"
      }
    }
  },
  menu_search: {
    input: { 
      dietary: "gluten-free", 
      meal_type: "all",
      allergens: "wheat"
    },
    output: { 
      gluten_free_items: [
        {
          name: "Street Bowl (No Tortilla)",
          price: "$11.99",
          description: "Rice bowl with choice of protein, beans, corn, pico de gallo and guacamole",
          popular: true
        },
        {
          name: "Grilled Chicken Salad",
          price: "$10.99",
          description: "Mixed greens with grilled chicken, avocado, tomatoes and citrus vinaigrette",
          popular: false
        }
      ],
      sides: [
        { name: "Side Salad", price: "$3.99" },
        { name: "Corn Chips & Guacamole", price: "$5.99" }
      ],
      allergen_info: "Our kitchen handles wheat products. We take precautions but cannot guarantee 100% gluten-free preparation."
    }
  },
  menu_highlights: {
    input: { 
      filter: "breakfast", 
      limit: 3,
      include_reviews: true
    },
    output: { 
      top_items: [
        { 
          name: "Weekend Breakfast Burrito",
          price: "$11.99",
          description: "Scrambled eggs, chorizo, potato, cheese and salsa wrapped in a large flour tortilla",
          rating: 4.8,
          reviews: ["Worth the wait!", "Absolutely massive and delicious"]
        },
        { 
          name: "Chicken & Waffles",
          price: "$13.99",
          description: "Crispy fried chicken on a Belgian waffle with maple hot honey",
          rating: 4.9,
          reviews: ["Perfect sweet and savory combo", "The hot honey is amazing"]
        },
        { 
          name: "Avocado Toast",
          price: "$9.99",
          description: "Multigrain toast with smashed avocado, cherry tomatoes and microgreens",
          rating: 4.7,
          reviews: ["So fresh!", "Best avocado toast in town"]
        }
      ],
      chef_recommendation: "Try our Breakfast Burrito with our house-made hot sauce!"
    }
  },
  location_info: {
    input: { 
      day: "weekly", 
      location_type: "schedule"
    },
    output: { 
      current_location: {
        address: "456 Beach Blvd Food Truck Park",
        hours: "8:00 AM - 2:00 PM"
      },
      upcoming_locations: [
        { day: "Monday", location: "Downtown Business District", hours: "11:00 AM - 2:00 PM" },
        { day: "Tuesday", location: "University Campus", hours: "10:00 AM - 3:00 PM" },
        { day: "Wednesday", location: "Tech Park", hours: "11:00 AM - 2:00 PM" },
        { day: "Thursday", location: "Farmer's Market", hours: "4:00 PM - 8:00 PM" },
        { day: "Friday", location: "Food Truck Friday Event", hours: "5:00 PM - 10:00 PM" }
      ],
      map_link: "https://maps.app.goo.gl/example"
    }
  }
};
